import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, concatMap, exhaustMap, map, mergeMap, tap} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {SharedLoginActions} from '@shared/components/login/store/shared-login.actions-type';
import {SharedLoginApiService} from '@shared/components/login/services/shared-login-api.service';
import {EAppType} from '@shared/models/AppType.enum';
import {Router} from '@angular/router';
import {of, withLatestFrom} from 'rxjs';
import {SharedLoginSelectors} from '@shared/components/login/store/shared-login.selector-type';
import {SharedLoginStorageService} from '@shared/components/login/services/shared-login-storage.service';
import {initialLoginState} from "@shared/components/login/store/shared-login.reducer";
import {AppPermissions, TokenDto} from "@server-models";
import {SharedPermissionsActions} from "@shared/stores/permissions/store/shared-permissions.actions-type";
import {SharedPermissionsService} from "@shared/services/shared-permissions.service";
import {SharedToastControllerService} from "@shared/services/shared-toast-controller.service";
import {TranslateService} from "@ngx-translate/core";

@Injectable({
  providedIn: 'root',
})
export class SharedLoginEffects {
  private _store = inject(Store);
  private _actions$ = inject(Actions);
  private _router = inject(Router);
  private _loginApiService = inject(SharedLoginApiService);
  private _sharedLoginStorage = inject(SharedLoginStorageService);
  private _permissionService = inject(SharedPermissionsService);
  private _toastService = inject(SharedToastControllerService);
  private _translate = inject(TranslateService);


  login$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SharedLoginActions.login),
      exhaustMap(({ email, passwordBase64 }) => {
        return this._loginApiService.login(email, passwordBase64).pipe(
          map((token) => SharedLoginActions.loginSuccess({ token })),
          catchError((error) => [SharedLoginActions.loginFail({ error })])
        );
      })
    )
  );

  loginByQuickAccessKey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SharedLoginActions.loginByQuickAccessKey),
      exhaustMap(({ key }) => {
        return this._loginApiService.loginByQuickAccessKey(key).pipe(
          mergeMap((token) => [
            SharedLoginActions.loginSuccess({ token })
          ]),
          catchError((error) => [SharedLoginActions.loginFail({ error })])
        );
      })
    )
  );

  getQuickAccessProfile$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SharedLoginActions.getQuickAccessProfile),
      exhaustMap(({ token }) => {
        return this._loginApiService.getQuickAccessProfile(token?.resourceId!).pipe(
          map((profile) =>
            SharedLoginActions.getQuickAccessProfileSuccess({ profile })
          ),
          catchError((error) => [SharedLoginActions.loginFail({ error })])
        );
      })
    )
  );

  loginFail$ = createEffect(() =>
      this._actions$.pipe(
        ofType(SharedLoginActions.loginFail),
        tap(() => this._router.navigate(['/']))
      )
    , { dispatch: false }
  );

  syncLoginStorage$ = createEffect(
    () => this._actions$.pipe(
      ofType(SharedLoginActions.syncLoginStorage),
      withLatestFrom(this._store.select(SharedLoginSelectors.selectLoginState)),
      concatMap(([, loginState]) => this._sharedLoginStorage.saveLogin(loginState))
    ),
    { dispatch: false }
  );


  loadLoginStorage$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SharedLoginActions.loadLoginStorage),
        withLatestFrom(this._store.select(SharedLoginSelectors.selectAppType)),
        mergeMap(async ([{resourceId}, appType]) => {
          try {
            return await this._sharedLoginStorage.loadLogin(appType, resourceId)
          } catch (error) {
            return initialLoginState
          }
        }),
        withLatestFrom(
          this._store.select(SharedLoginSelectors.selectAppType),
          this._store.select(SharedLoginSelectors.selectClient),
          this._store.select(SharedLoginSelectors.selectAppSegment),
          this._store.select(SharedLoginSelectors.selectBrand)
        ),
        map(([storedLoginState, currentApp, currentClient, currentAppSegment, currentBrand]) => {
          // Check storage consistency
          const isStoredLoginInvalid =
            currentApp !== storedLoginState?.currentApp ||
            !storedLoginState?.currentToken ||
            !storedLoginState?.currentTenant ||
            (currentApp === EAppType.Orga && !storedLoginState?.currentLocation);

          // Overtake either storage login state or initial login state based on storage validity and keep currentApp and its children
          const validState = {
            ...(isStoredLoginInvalid ? initialLoginState : storedLoginState),
            currentApp,
            currentClient,
            currentAppSegment,
            currentBrand: currentBrand ?? storedLoginState?.currentBrand
          };
          return SharedLoginActions.loadLoginStorageSuccess({ loadedState: validState })
        })
      )
  );

  getTenantSettings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SharedLoginActions.getTenantSettings),
      mergeMap(({ tenantId }) => this._loginApiService
        .getAppSettings(tenantId).pipe(
          map((tenantSettings) => SharedLoginActions.getTenantSettingsSuccess({ tenantSettings })
          )
        )
      )
    )
  );

  loginRefresh$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SharedLoginActions.refreshToken),
      withLatestFrom(
        this._store.select(SharedLoginSelectors.selectToken),
        this._store.select(SharedLoginSelectors.selectTenant),
        this._store.select(SharedLoginSelectors.selectClient)
      ),
      exhaustMap(([{ tenantId }, token, tenant, client]) => {
        return this._loginApiService.refreshToken(token?.refreshToken!, tenant?.tenantId! ?? tenantId, client).pipe(
          map((newToken: TokenDto) => {
            return SharedLoginActions.refreshTokenSuccess({ newToken, tenantId });
          }),
          catchError((error) => [SharedLoginActions.refreshTokenFail({ error })])
        );
      })
    )
  );

  loginRefreshSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SharedLoginActions.refreshTokenSuccess),
      withLatestFrom(
        this._store.select(SharedLoginSelectors.selectTenant),
      ),
      mergeMap(([{ newToken, tenantId }, oldTenant]) => {
          const newTenant = newToken?.info?.tenants?.find(t => t.tenantId === tenantId);
          if (!!tenantId && !oldTenant && !!newTenant) {
            return [
              SharedLoginActions.setTenant({ tenant: newTenant }),
              SharedLoginActions.syncLoginStorage(),
              SharedPermissionsActions.getPermissions({ token: newToken })
            ]
          } else {
            return [
              SharedLoginActions.syncLoginStorage(),
              SharedPermissionsActions.getPermissions({ token: newToken })
            ]
          }
        }
      ))
  );

  loginRefreshFail$ = createEffect(() =>
      this._actions$.pipe(
        ofType(SharedLoginActions.refreshTokenFail),
        mergeMap((error) => this._router.navigate(['not-allowed']))
      )
    , { dispatch: false });

  checkAppPermission$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(SharedLoginActions.checkAppPermission),
      withLatestFrom(this._store.select(SharedLoginSelectors.selectAppType)),
      mergeMap(([_, selectedApp]) =>
        this._permissionService
          .hasPermissions$([
            selectedApp === EAppType.Tech ? AppPermissions.DisplayTechProduct : AppPermissions.DisplayTeamProduct,
          ])
          .pipe(
            map((canUseApp) => {
              // Team or Tech needs display permission
              // Link is not necessarily permitted
              if (canUseApp || selectedApp === EAppType.Link) {
                return SharedLoginActions.checkAppPermissionSuccess();
              } else {
                return SharedLoginActions.checkAppPermissionFail();
              }
            })
          )
      )
    );
  });

  checkAppPermissionFail$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SharedLoginActions.checkAppPermissionFail),
        withLatestFrom(
          this._store.select(SharedLoginSelectors.selectAppType),
          this._store.select(SharedLoginSelectors.selectAppSegment),
          this._store.select(SharedLoginSelectors.selectIsGuest)
        ),
        mergeMap(async ([_, appType, appSegment, isGuest]) => {
          // Team guests should see the welcome page
          if (appType === EAppType.Team && isGuest) {
            return this._router.navigate([appSegment, 'welcome'])
          } else {
            return this._router.navigate(['not-allowed'])
          }
        })
      ),
    { dispatch: false }
  );

  checkAppPermissionSuccess$ = createEffect(
    () => this._actions$.pipe(ofType(SharedLoginActions.checkAppPermissionSuccess)),
    { dispatch: false }
  );

  changePasswordStart$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SharedLoginActions.changePassword),
      exhaustMap((action) => {
        return this._loginApiService
          .changePassword(action.email, action.oldPasswordBase64, action.newPasswordBase64)
          .pipe(
            map(() => SharedLoginActions.changePasswordSuccess()),
            catchError((error) => of(SharedLoginActions.changePasswordFail({ error })))
          );
      })
    )
  );

  changePasswordSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SharedLoginActions.changePasswordSuccess),
      withLatestFrom(
        this._store.select(SharedLoginSelectors.selectEmail),
        this._store.select(SharedLoginSelectors.selectPassword)
      ),
      exhaustMap(([_, tempEmail, tempPassword]) => {
        const successMessage = this._translate.instant('LOGIN.CHANGE_PASSWORD.TOASTS.SUCCESS');
        this._toastService.present(successMessage).then();
        // Login with changed credentials
        return [SharedLoginActions.login({ email: tempEmail ?? '', passwordBase64: tempPassword ?? '' })];
      })
    )
  );

  logout$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SharedLoginActions.logout),
        withLatestFrom(this._store.select(SharedLoginSelectors.selectLoginState)),
        exhaustMap(async ([, loginState]) => {
          await this._sharedLoginStorage.removeLogin(loginState);
          await this._router.navigate(['/']).then(() => window.location.reload());
        })
      ),
    { dispatch: false }
  );
}
